import type { Workspace, Account } from "./types";

/* Churn & expansion prediction — client mirror of api/src/reporting/predict.ts.
 * A transparent, explainable scoring model over the signals already in the
 * workspace: each account gets a churn-risk probability and an expansion
 * propensity, each with the ranked drivers that moved it, so a CSM can see
 * *why*. Kept byte-compatible with the API so offline demo matches the server. */

export interface Driver { label: string; impact: number; detail: string; }
export interface Prediction {
  accountId: string; accountName: string; arr: number; ownerId: string;
  churnRisk: number;        // 0..100 probability of churn
  expansion: number;        // 0..100 propensity to expand
  churnDrivers: Driver[];
  expansionDrivers: Driver[];
  band: "low" | "medium" | "high"; // churn band
}

const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));
const DAY = 86_400_000;

export function predictAccount(ws: Workspace, a: Account, today = "2026-08-20"): Prediction {
  const now = +new Date(today);
  const sigs = ws.signals.filter((s) => s.accountId === a.id);
  const util = a.seats ? a.seatsActive / a.seats : 0;
  const daysToRenew = (+new Date(a.renewalDate) - now) / DAY;

  // ---- churn risk ----
  const cd: Driver[] = [];
  let churn = 40 - (a.health - 50) * 0.9; // health is the anchor
  cd.push({ label: "Account health", impact: Math.round(-(a.health - 50) * 0.9), detail: `Health ${a.health}/100` });

  if (util < 0.5) { const i = Math.round((0.5 - util) * 60); churn += i; cd.push({ label: "Low seat utilization", impact: i, detail: `${Math.round(util * 100)}% of seats active` }); }
  const risky = sigs.filter((s) => s.band === "risk").length;
  if (risky) { const i = risky * 9; churn += i; cd.push({ label: "Open risk signals", impact: i, detail: `${risky} active risk signal${risky > 1 ? "s" : ""}` }); }
  if (a.attributes?.sponsor_present === false) { churn += 10; cd.push({ label: "No executive sponsor", impact: 10, detail: "Sponsor not present" }); }
  if (daysToRenew >= 0 && daysToRenew <= 90) { const i = 8; churn += i; cd.push({ label: "Renewal window", impact: i, detail: `Renews in ${Math.round(daysToRenew)} days` }); }
  const trendDelta = a.healthTrend?.length ? a.healthTrend[a.healthTrend.length - 1] - a.healthTrend[0] : 0;
  if (trendDelta < 0) { const i = Math.round(-trendDelta * 0.5); churn += i; cd.push({ label: "Declining health trend", impact: i, detail: `${trendDelta} over 12 weeks` }); }

  // ---- expansion propensity ----
  const ed: Driver[] = [];
  let exp = a.expansionPotential ?? 0;
  ed.push({ label: "Expansion potential", impact: Math.round(a.expansionPotential ?? 0), detail: `${a.expansionPotential ?? 0}% modeled headroom` });
  if (a.health >= 70) { const i = Math.round((a.health - 70) * 0.8); exp += i; ed.push({ label: "Strong health", impact: i, detail: `Health ${a.health}` }); }
  if (util >= 0.8) { const i = Math.round((util - 0.8) * 80); exp += i; ed.push({ label: "High utilization", impact: i, detail: `${Math.round(util * 100)}% seats active` }); }
  const positive = sigs.filter((s) => s.band === "positive").length;
  if (positive) { const i = positive * 10; exp += i; ed.push({ label: "Positive signals", impact: i, detail: `${positive} expansion signal${positive > 1 ? "s" : ""}` }); }
  if (a.attributes?.sponsor_present === true) { exp += 6; ed.push({ label: "Executive sponsor", impact: 6, detail: "Sponsor engaged" }); }

  const churnRisk = clamp(churn);
  return {
    accountId: a.id, accountName: a.name, arr: a.arr, ownerId: a.ownerId,
    churnRisk, expansion: clamp(exp),
    churnDrivers: cd.filter((d) => d.impact !== 0).sort((x, y) => y.impact - x.impact),
    expansionDrivers: ed.filter((d) => d.impact !== 0).sort((x, y) => y.impact - x.impact),
    band: churnRisk >= 60 ? "high" : churnRisk >= 35 ? "medium" : "low",
  };
}

export function predictAll(ws: Workspace, today = "2026-08-20"): Prediction[] {
  return ws.accounts.map((a) => predictAccount(ws, a, today)).sort((a, b) => b.churnRisk - a.churnRisk);
}
